import {useEffect, useRef, useState} from 'react'
import {isAxiosError} from 'axios'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import {ChatMessage, DocSection, LUWIE_AVATAR_URL} from '../core/_models'
import {buildContext, detectSmallTalk, getSections, searchSections} from '../core/_knowledge'
import {askLuwie} from '../core/_requests'

const WELCOME: ChatMessage = {
  role: 'assistant',
  content: 'Halo! Saya LUWIE, asisten virtual Alaya. Tanya apa saja tentang aplikasi ini.',
}

const FALLBACK_ERROR = 'Maaf, LUWIE sedang tidak tersedia. Coba lagi nanti.'

type Props = {
  onClose: () => void
  initialQuestion?: string
}

export function LuwieChatPanel({onClose, initialQuestion}: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [sections, setSections] = useState<DocSection[]>([])
  const bodyRef = useRef<HTMLDivElement>(null)

  // Prefill pertanyaan yang dikirim dari search box header (event 'luwie:open')
  useEffect(() => {
    if (initialQuestion) {
      setInput(initialQuestion)
    }
  }, [initialQuestion])

  useEffect(() => {
    let mounted = true
    getSections()
      .then((s) => {
        if (mounted) setSections(s)
      })
      .catch((err) => console.error('[luwie] gagal memuat docs', err))
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    const el = bodyRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, loading])

  const MAX_HISTORY_MESSAGES = 10

  const handleSend = async () => {
    const question = input.trim()
    if (!question || loading) return

    // Ambil beberapa pesan terakhir SEBELUM pesan baru ini ditambahkan, biar
    // backend tahu konteks percakapan sejauh ini (LUWIE tidak simpan riwayat
    // sendiri — tiap request stateless kecuali history dikirim eksplisit).
    const history = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(-MAX_HISTORY_MESSAGES)

    setInput('')
    setMessages((prev) => [...prev, {role: 'user', content: question}])

    // Sapaan/small talk dijawab lokal — instan, tanpa biaya token AI.
    const smallTalk = detectSmallTalk(question)
    if (smallTalk) {
      setMessages((prev) => [...prev, {role: 'assistant', content: smallTalk}])
      return
    }

    setLoading(true)

    try {
      const matched = searchSections(question, sections)
      const context = buildContext(matched)
      const data = await askLuwie({question, context, history})
      const answer = data?.response?.answer || data?.response?.data?.answer
      const text = answer && answer.trim() ? answer.trim() : FALLBACK_ERROR
      setMessages((prev) => [...prev, {role: 'assistant', content: text}])
    } catch (err) {
      console.error('[luwie] gagal menjawab', err)
      const msg = isAxiosError(err)
        ? err?.response?.data?.response?.message_en || FALLBACK_ERROR
        : FALLBACK_ERROR
      setMessages((prev) => [...prev, {role: 'assistant', content: msg}])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='d-flex flex-column h-100'>
      <div className='d-flex align-items-center justify-content-between px-4 py-3 border-bottom'>
        <div className='d-flex align-items-center'>
          <div className='symbol symbol-circle symbol-35px me-3'>
            <img src={LUWIE_AVATAR_URL} alt='LUWIE' />
          </div>
          <div>
            <div className='fs-6 fw-bold text-gray-900'>LUWIE</div>
            <div className='fs-8 fw-semibold text-gray-400'>Asisten virtual Alaya</div>
          </div>
        </div>
        <button
          type='button'
          className='btn btn-sm btn-icon btn-active-light-primary'
          onClick={onClose}
          aria-label='Tutup'
        >
          <i className='ki-duotone ki-cross fs-2'>
            <span className='path1' />
            <span className='path2' />
          </i>
        </button>
      </div>

      <div
        ref={bodyRef}
        className='flex-grow-1 overflow-auto px-4 py-4'
        style={{background: '#f5f8fa'}}
      >
        {messages.map((m, i) => (
          <div key={i} className={`d-flex mb-3 ${m.role === 'user' ? 'justify-content-end' : ''}`}>
            <div
              className='px-4 py-2 text-gray-800 luwie-markdown'
              style={{
                maxWidth: '80%',
                borderRadius: 12,
                background: m.role === 'user' ? '#e4e6ef' : '#ffffff',
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{m.content}</ReactMarkdown>
            </div>
          </div>
        ))}
        {loading && (
          <div className='d-flex mb-3'>
            <div className='px-4 py-2 text-gray-400'>LUWIE mengetik…</div>
          </div>
        )}
      </div>

      <div className='d-flex align-items-center gap-2 px-4 py-3 border-top'>
        <input
          type='text'
          className='form-control form-control-solid'
          placeholder='Tulis pertanyaan…'
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSend()
          }}
        />
        <button
          type='button'
          className='btn btn-primary'
          onClick={handleSend}
          disabled={loading || !input.trim()}
        >
          Kirim
        </button>
      </div>
    </div>
  )
}
